import React, { useState, useRef, useEffect } from "react";
import type { SelectOption } from "../types";

interface Props<T extends string> {
  options: SelectOption<T>[];
  value: T[];
  onChange: (v: T[]) => void;
  placeholder?: string;
  className?: string;
  singleSelect?: boolean; // If true, behaves like a single-select dropdown
}
export function DropdownMultiSelectNoSelectAll<T extends string>({
  options,
  value,
  onChange,
  placeholder = "Select...",
  className = "",
  singleSelect = false,
}: Props<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (val: T) => {
    if (singleSelect) {
      onChange([val]);
      setIsOpen(false);
    } else {
      onChange(
        value.includes(val) ? value.filter((v) => v !== val) : [...value, val],
      );
    }
  };

  // Determine button text based on selection
  let buttonText = placeholder;
  if (value.length > 0) {
    if (singleSelect) {
      const selectedOpt = options.find((o) => o.value === value[0]);
      buttonText = selectedOpt ? selectedOpt.label : placeholder;
    } else if (value.length === 1) {
      const selectedOpt = options.find((o) => o.value === value[0]);
      buttonText = selectedOpt ? selectedOpt.label : placeholder;
    } else {
      buttonText = `${value.length} Selected`;
    }
  }

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Trigger Button (styled exactly like .dd-select) */}
      <button
        type="button"
        className={`dd-multiselect-trigger w-full flex items-center justify-between text-left text-sm whitespace-nowrap border rounded px-3 py-2 ${isOpen ? "text-primary" : "border-gray-300"}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{ minHeight: "38px", background: "#fff" }}
      >
        <span className="truncate flex-1 max-w-[150px] overflow-hidden text-ellipsis">
          {buttonText}
        </span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform text-gray-500 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Popover Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full min-w-[200px] bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden max-h-64 overflow-y-auto">
          {/* Options */}
          {options.map((opt, idx) => {
            const isSelected = value.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={`flex items-center px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                  idx < options.length - 1 ? "border-b border-gray-50" : ""
                } ${singleSelect && isSelected ? "bg-orange-50" : ""}`}
                style={{ userSelect: "none" }}
                onClick={() => {
                  if (singleSelect) {
                    handleToggle(opt.value);
                  }
                }}
              >
                {!singleSelect && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggle(opt.value)}
                    className="w-4 h-4 mr-3 text-primary cursor-pointer rounded"
                  />
                )}
                {singleSelect && (
                  <span
                    className={`w-4 h-4 mr-3 inline-block ${isSelected ? "bg-orange-500 rounded-full border border-orange-500" : "border border-gray-300 rounded-full"}`}
                    style={{ minWidth: 16 }}
                  />
                )}
                <span
                  className={`text-sm ${isSelected ? "text-orange-600 font-medium" : "text-gray-600"} ${singleSelect ? "pl-0" : ""}`}
                  style={{ cursor: "pointer" }}
                >
                  {opt.label}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
